import { useEffect, useMemo, useState } from 'react'
import { useApiFetch } from '@/hooks/useApiFetch'
import { useApiMutation } from '@/hooks/useApiMutation'
import { useToast } from '@/components/ToastContainer'
import { SHARED_RANGE_PRESETS_WITH_ALL, ALL_PRESET_KEY } from '@/lib/dateRange'
import {
  Table,
  Pagination,
  StatCard,
  DateRangePicker,
  Input,
  Textarea,
  Button,
  Badge,
  Alert,
  Drawer,
  Modal,
  Dropdown,
  EmptyState,
  Card,
  ConfirmDialog,
  type TableColumn,
} from '@/ui'
import { StatusBadge } from '@/components/StatusBadge'
import { formatDate, formatCurrency, formatNumber } from '@/ui'
import styles from './SmartstoreEvents.module.css'

// ============================================================
// 타입 (백엔드 응답 미러링)
// ============================================================

interface ActivationCodeSnippet {
  id: string
  code: string
  status: string
  usedAt: string | null
  expiresAt: string | null
}

interface PaymentSnippet {
  id: string
  userId: string | null
  type: string
  status: string
  amount: number
  creditsGranted: number | null
  createdAt: string
}

interface PaginationMeta {
  page: number
  pageSize: number
  total: number
  totalPages: number
  hasNext: boolean
  hasPrev: boolean
}

type Category = 'PAYED' | 'CANCEL' | 'RETURN' | 'EXCHANGE' | 'OTHER'

interface OrderItem {
  productOrderId: string
  orderId: string
  productOrderStatus: string
  category: Category
  productName: string
  optionCode: string
  productClass?: string
  productOption: string | null
  quantity: number
  amount: number
  creditsPerUnit: number | null
  activates: boolean | null
  mappingMissing: boolean
  ordererName: string | null
  ordererTel: string | null
  activationCode: ActivationCodeSnippet | null
  payment: PaymentSnippet | null
}

interface OrdersResponse {
  orders: OrderItem[]
  pagination: PaginationMeta
  summary: {
    total: number
    totalAmount: number
    byCategory: Record<string, number>
    byStatus: Record<string, number>
  }
  range: { from: string; to: string }
}

interface LookupResponse {
  orders: OrderItem[]
}

// ============================================================
// 공통 유틸
// ============================================================

const CATEGORY_LABEL: Record<Category, string> = {
  PAYED: '결제',
  CANCEL: '취소',
  RETURN: '반품',
  EXCHANGE: '교환',
  OTHER: '기타',
}

const CATEGORY_COLOR: Record<Category, 'blue' | 'red' | 'yellow' | 'gray'> = {
  PAYED: 'blue',
  CANCEL: 'red',
  RETURN: 'yellow',
  EXCHANGE: 'blue',
  OTHER: 'gray',
}

const STATUS_LABEL: Record<string, string> = {
  PAYED: '결제완료',
  DISPATCHED: '발송처리',
  DELIVERING: '배송중',
  DELIVERED: '배송완료',
  PURCHASE_DECIDED: '구매확정',
  CANCEL_REQUESTED: '취소요청',
  CANCELED: '취소',
  RETURN_REQUESTED: '반품요청',
  RETURNED: '반품',
  EXCHANGE_REQUESTED: '교환요청',
  EXCHANGED: '교환',
  PAYMENT_WAITING: '미입금',
}

const ACT_CODE_COLOR: Record<string, 'blue' | 'gray' | 'red' | 'yellow' | 'green'> = {
  AVAILABLE: 'blue',
  USED: 'gray',
  EXPIRED: 'red',
}

const FILTER_CATEGORIES: Category[] = ['PAYED', 'CANCEL', 'RETURN', 'EXCHANGE']

function statusBadgeColor(
  status: string,
): 'blue' | 'green' | 'gray' | 'red' | 'yellow' {
  if (status === 'PAYED') return 'blue'
  if (status === 'DISPATCHED' || status === 'DELIVERING') return 'yellow'
  if (status === 'DELIVERED' || status === 'PURCHASE_DECIDED') return 'green'
  if (status.startsWith('CANCEL') || status === 'RETURNED' || status === 'EXCHANGED') return 'red'
  return 'gray'
}

function ActivationCodeCell({ code }: { code: ActivationCodeSnippet | null }) {
  if (!code) {
    return <span className={styles.muted}>미발급</span>
  }
  const color = ACT_CODE_COLOR[code.status] ?? 'gray'
  return (
    <div className={styles.codeCell}>
      <code className={styles.codeText}>{code.code}</code>
      <Badge color={color}>{code.status}</Badge>
    </div>
  )
}

function PaymentCell({ payment }: { payment: PaymentSnippet | null }) {
  if (!payment) return <span className={styles.muted}>—</span>
  return <StatusBadge status={payment.status} />
}

function dateToFromIso(d: string | null | undefined): string | undefined {
  if (!d) return undefined
  return `${d}T00:00:00.000Z`
}

function dateToEndIso(d: string | null | undefined): string | undefined {
  if (!d) return undefined
  return `${d}T23:59:59.999Z`
}

function defaultDate(daysAgo: number): string {
  const d = new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000)
  return d.toISOString().slice(0, 10)
}

function todayDate(): string {
  return new Date().toISOString().slice(0, 10)
}

// ============================================================
// 메인 페이지 — 통합 리스트 1개
// ============================================================

// 빠른검색 프리셋 — admin 공용(SHARED_RANGE_PRESETS_WITH_ALL). '전체'는 기간 해제.

export function SmartstoreEvents() {
  // draft: 사용자가 입력 중인 값. applied: 검색 버튼으로 확정되어 fetch에 반영된 값
  const [draftFrom, setDraftFrom] = useState<string>(() => defaultDate(1))
  const [draftTo, setDraftTo] = useState<string>(() => todayDate())
  const [draftCategories, setDraftCategories] = useState<Category[]>([...FILTER_CATEGORIES])
  const [appliedFrom, setAppliedFrom] = useState<string>(draftFrom)
  const [appliedTo, setAppliedTo] = useState<string>(draftTo)
  const [appliedCategories, setAppliedCategories] = useState<Category[]>([...FILTER_CATEGORIES])
  const [page, setPage] = useState(1)

  // 검색어 — debounce 없이 바로 백엔드에 전달 (q 파라미터)
  const [searchInput, setSearchInput] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')

  const [selected, setSelected] = useState<OrderItem | null>(null)

  const params = useMemo(() => {
    const p = new URLSearchParams()
    p.set('page', String(page))
    p.set('pageSize', '50')
    const f = dateToFromIso(appliedFrom)
    const t = dateToEndIso(appliedTo)
    if (f) p.set('from', f)
    if (t) p.set('to', t)
    if (appliedCategories.length > 0 && appliedCategories.length < FILTER_CATEGORIES.length) {
      p.set('categories', appliedCategories.join(','))
    }
    if (appliedSearch.trim()) p.set('q', appliedSearch.trim())
    return p
  }, [page, appliedFrom, appliedTo, appliedCategories, appliedSearch])

  const path = `/api/admin/smartstore/orders?${params.toString()}`
  const { data, isLoading, error, refetch } = useApiFetch<OrdersResponse>(path)

  // ============================================================
  // 검색 fallback — 기간 내 결과가 없고 검색어가 있을 때
  // Naver API 직조회로 기간 외 주문도 찾을 수 있게 한다.
  // ============================================================
  const lookupPath = useMemo(() => {
    const q = appliedSearch.trim()
    if (!q) return null
    if ((data?.summary.total ?? 0) > 0) return null // 기간 내 결과 있으면 fallback 불필요
    return `/api/admin/smartstore/orders/lookup?orderId=${encodeURIComponent(q)}`
  }, [appliedSearch, data?.summary.total])

  const { data: lookupData, isLoading: lookupLoading } = useApiFetch<LookupResponse>(lookupPath)

  const toggleCategory = (cat: Category) => {
    setDraftCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    )
  }

  const handleSearch = () => {
    setAppliedFrom(draftFrom)
    setAppliedTo(draftTo)
    setAppliedCategories([...draftCategories])
    setAppliedSearch(searchInput)
    setPage(1)
  }

  const handleRangeChange = (
    range: { from: string | null; to: string | null },
    meta: { presetKey: string | null },
  ) => {
    // '전체' 프리셋 = 기간 필터 해제 후 즉시 적용
    if (meta.presetKey === ALL_PRESET_KEY) {
      setDraftFrom('')
      setDraftTo('')
      setAppliedFrom('')
      setAppliedTo('')
      setAppliedCategories([...draftCategories])
      setAppliedSearch(searchInput)
      setPage(1)
      return
    }
    if (!range.from || !range.to) return
    // 프리셋(빠른검색)은 즉시 적용, 커스텀 범위는 draft 만 갱신 후 '조회'로 확정.
    setDraftFrom(range.from)
    setDraftTo(range.to)
    if (meta.presetKey) {
      setAppliedFrom(range.from)
      setAppliedTo(range.to)
      setAppliedCategories([...draftCategories])
      setAppliedSearch(searchInput)
      setPage(1)
    }
  }

  // 검색 input 비우면 즉시 q 해제
  useEffect(() => {
    if (searchInput === '' && appliedSearch !== '') {
      setAppliedSearch('')
      setPage(1)
    }
  }, [searchInput, appliedSearch])

  const [actionRequest, setActionRequest] = useState<ClaimActionRequest | null>(null)

  const columns: TableColumn<OrderItem>[] = [
    {
      key: 'category', label: '구분',
      render: (r) => <Badge color={CATEGORY_COLOR[r.category]}>{CATEGORY_LABEL[r.category]}</Badge>,
      width: '70px',
    },
    {
      key: 'productOrderStatus', label: '상태',
      render: (r) => <Badge color={statusBadgeColor(r.productOrderStatus)}>{STATUS_LABEL[r.productOrderStatus] ?? r.productOrderStatus}</Badge>,
      width: '110px',
    },
    {
      key: 'orderId', label: '주문/상품주문번호',
      render: (r) => (
        <div className={styles.idStack}>
          <code className={styles.codeText}>{r.orderId}</code>
          <code className={styles.codeTextSecondary}>{r.productOrderId}</code>
        </div>
      ),
      width: '180px',
    },
    {
      key: 'productName', label: '상품',
      render: (r) => (
        <div className={styles.productCell}>
          <span>{r.productName}</span>
          {r.productOption && <span className={styles.muted}>{r.productOption}</span>}
          {r.mappingMissing && <Badge color="red">매핑 누락</Badge>}
        </div>
      ),
    },
    { key: 'amount', label: '금액', render: (r) => formatCurrency(r.amount), align: 'right' as const, width: '100px' },
    { key: 'quantity', label: '수량', render: (r) => `${r.quantity}`, align: 'right' as const, width: '60px' },
    { key: 'ordererName', label: '주문자', render: (r) => r.ordererName ?? '—', width: '90px' },
    {
      key: 'activationCode', label: '활성화 코드',
      render: (r) => <ActivationCodeCell code={r.activationCode} />,
    },
    {
      key: 'payment', label: '결제',
      render: (r) => <PaymentCell payment={r.payment} />,
      width: '90px',
    },
    {
      key: 'actions', label: '처리',
      render: (r) => <RowActions item={r} onClaim={(action) => setActionRequest({ event: r, action })} onRefresh={() => refetch()} />,
      width: '100px',
    },
  ]

  // 표시할 행: 일반 결과 + 빈 결과 fallback (Naver 직조회)
  const showingFallback = !!lookupData?.orders && lookupData.orders.length > 0 && (data?.summary.total ?? 0) === 0
  const rows = showingFallback ? lookupData!.orders : data?.orders ?? []

  return (
    <div className={styles.root}>
      <header className={styles.header}>
        <h1 className={styles.title}>스마트스토어</h1>
        <p className={styles.subtitle}>네이버 커머스 API 실시간 조회 + DB 매칭</p>
      </header>

      <div className={styles.statGrid}>
        <StatCard label="총 결제금액" value={formatCurrency(data?.summary.totalAmount ?? 0)} />
        <StatCard label="총 건수" value={`${formatNumber(data?.summary.total ?? 0)}건`} />
        {FILTER_CATEGORIES.map((cat) => (
          <StatCard key={cat} label={CATEGORY_LABEL[cat]} value={`${formatNumber(data?.summary.byCategory[cat] ?? 0)}건`} />
        ))}
      </div>

      <div className={styles.filters}>
        <DateRangePicker
          size="sm"
          value={{ from: draftFrom || null, to: draftTo || null }}
          presets={SHARED_RANGE_PRESETS_WITH_ALL}
          onChange={handleRangeChange}
        />
        <div className={styles.typeFilter}>
          {FILTER_CATEGORIES.map((cat) => (
            <Button
              key={cat}
              size="sm"
              variant={draftCategories.includes(cat) ? 'primary' : 'ghost'}
              onClick={() => toggleCategory(cat)}
            >
              {CATEGORY_LABEL[cat]}
            </Button>
          ))}
        </div>
        <Button variant="primary" size="sm" onClick={handleSearch} disabled={isLoading}>
          조회
        </Button>
        <Button variant="secondary" size="sm" onClick={() => refetch()} disabled={isLoading}>
          새로고침
        </Button>
      </div>

      <div className={styles.searchRow}>
        <Input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="주문번호 / 상품주문번호 / 주문자 / 상품명 검색"
          size="sm"
          onKeyDown={(e) => { if (e.key === 'Enter') handleSearch() }}
        />
        {appliedSearch && (
          <span className={styles.muted}>
            "{appliedSearch}"
            {showingFallback && ' — 기간 외 Naver 직조회 결과'}
          </span>
        )}
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {!isLoading && !lookupLoading && rows.length === 0 ? (
        <EmptyState
          title="조회된 주문이 없습니다"
          description={appliedSearch ? '다른 검색어를 시도하거나 기간을 조정하세요.' : '기간/카테고리를 조정해 다시 시도하세요.'}
        />
      ) : (
        <Table<OrderItem>
          columns={columns}
          data={rows}
          rowKey={(r) => r.productOrderId}
          isLoading={isLoading || lookupLoading}
          onRowClick={(r) => setSelected(r)}
        />
      )}

      {!showingFallback && data?.pagination && data.pagination.totalPages > 1 && (
        <Pagination current={data.pagination.page} total={data.pagination.totalPages} onChange={setPage} size="sm" />
      )}

      <Drawer open={!!selected} onClose={() => setSelected(null)} title={selected ? `상품주문 ${selected.productOrderId}` : ''} size="lg">
        {selected && <OrderDetail item={selected} onRefresh={() => { refetch(); setSelected(null) }} />}
      </Drawer>

      <ClaimActionDialog request={actionRequest} onClose={() => setActionRequest(null)} onSuccess={() => { setActionRequest(null); refetch() }} />
    </div>
  )
}

// ============================================================
// 행 액션 — PAYED 면 발송, 클레임이면 처리 메뉴
// ============================================================

function RowActions({
  item,
  onClaim,
  onRefresh,
}: {
  item: OrderItem
  onClaim: (action: ClaimActionType) => void
  onRefresh: () => void
}) {
  if (item.productOrderStatus === 'PAYED') {
    return <DispatchButton item={item} onSuccess={onRefresh} compact />
  }

  if (item.category === 'CANCEL' || item.category === 'RETURN' || item.category === 'EXCHANGE') {
    const items = [
      { label: '승인', onClick: () => onClaim('approve') },
      { label: '거부', onClick: () => onClaim('reject') },
    ]
    if (item.category === 'RETURN' || item.category === 'EXCHANGE') {
      items.push({ label: '보류', onClick: () => onClaim('holdback') })
      items.push({ label: '보류 해제', onClick: () => onClaim('release-holdback') })
    }
    return (
      <Dropdown
        trigger={<Button size="sm" variant="ghost">처리 ▾</Button>}
        items={items}
        align="right"
      />
    )
  }

  return <span className={styles.muted}>—</span>
}

// ============================================================
// Drawer — 상세 (메타 + 액션)
// ============================================================

function OrderDetail({ item, onRefresh }: { item: OrderItem; onRefresh: () => void }) {
  return (
    <div className={styles.detail}>
      <div className={styles.metaGrid}>
        <Meta label="구분" value={CATEGORY_LABEL[item.category]} />
        <Meta label="상태" value={STATUS_LABEL[item.productOrderStatus] ?? item.productOrderStatus} />
        <Meta label="주문번호" value={item.orderId} />
        <Meta label="상품주문번호" value={item.productOrderId} />
        <Meta label="금액" value={formatCurrency(item.amount)} />
        <Meta label="수량" value={`${item.quantity}`} />
        <Meta label="크레딧/단위" value={item.creditsPerUnit !== null ? `${item.creditsPerUnit} (×${item.quantity})` : '—'} />
        <Meta label="주문자" value={item.ordererName ?? '—'} />
        <Meta label="연락처" value={item.ordererTel ?? '—'} />
        <Meta label="옵션코드" value={item.optionCode} />
      </div>

      <Card>
        <div className={styles.itemHeader}>
          <code className={styles.codeText}>{item.productOrderId}</code>
          <Badge color={statusBadgeColor(item.productOrderStatus)}>
            {STATUS_LABEL[item.productOrderStatus] ?? item.productOrderStatus}
          </Badge>
          {item.mappingMissing && <Badge color="red">매핑 누락</Badge>}
        </div>
        <div className={styles.itemBody}>
          <div className={styles.itemName}>
            {item.productName}
            {item.productOption && <span className={styles.muted}> · {item.productOption}</span>}
          </div>
          <div className={styles.itemMetaRow}>
            <span>활성화 코드:</span>
            <ActivationCodeCell code={item.activationCode} />
          </div>
          <div className={styles.itemMetaRow}>
            <span>결제:</span>
            <PaymentCell payment={item.payment} />
            {item.payment && (
              <span className={styles.muted}>
                크레딧 {item.payment.creditsGranted ?? 0} · {formatDate(item.payment.createdAt, 'yyyy.M.d HH:mm')}
              </span>
            )}
          </div>
          {item.productOrderStatus === 'PAYED' && (
            <DispatchButton item={item} onSuccess={onRefresh} />
          )}
        </div>
      </Card>
    </div>
  )
}

// ============================================================
// 발송 처리 액션 (PAYED 전용)
// ============================================================

function DispatchButton({ item, onSuccess, compact }: {
  item: OrderItem
  onSuccess: () => void
  compact?: boolean
}) {
  const { addToast } = useToast()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const path = `/api/admin/smartstore/orders/${encodeURIComponent(item.productOrderId)}/dispatch`
  const { trigger, isLoading } = useApiMutation<{ activationCodeUsed: string | null }>(path, 'POST')

  const handleConfirm = async () => {
    const result = await trigger()
    if (result) {
      addToast({ type: 'success', message: '발송처리 완료 — 잠시 후 상태가 갱신됩니다' })
      setConfirmOpen(false)
      onSuccess()
    } else {
      addToast({ type: 'error', message: '발송처리 실패 — 네트워크 탭 또는 서버 로그 확인' })
    }
  }

  return (
    <>
      <Button
        size="sm"
        variant={compact ? 'ghost' : 'primary'}
        onClick={(e) => { e.stopPropagation(); setConfirmOpen(true) }}
        disabled={isLoading}
        loading={isLoading}
      >
        발송
      </Button>
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
        title="발송처리"
        message={`상품주문 ${item.productOrderId} 을(를) 발송처리합니다. 네이버 커머스에 즉시 반영됩니다.`}
        confirmLabel="발송처리"
        loading={isLoading}
      />
    </>
  )
}

// ============================================================
// 클레임 처리 다이얼로그
// ============================================================

type ClaimActionType = 'approve' | 'reject' | 'holdback' | 'release-holdback'

interface ClaimActionRequest {
  event: OrderItem
  action: ClaimActionType
}

const ACTION_LABEL: Record<ClaimActionType, string> = {
  approve: '승인',
  reject: '거부',
  holdback: '보류',
  'release-holdback': '보류 해제',
}

function ClaimActionDialog({
  request,
  onClose,
  onSuccess,
}: {
  request: ClaimActionRequest | null
  onClose: () => void
  onSuccess: () => void
}) {
  const { addToast } = useToast()
  const [reason, setReason] = useState('')
  const [holdbackClassType, setHoldbackClassType] = useState('')

  const productOrderId = request?.event.productOrderId ?? ''
  const path = productOrderId
    ? `/api/admin/smartstore/orders/${encodeURIComponent(productOrderId)}/claim-action`
    : '/api/admin/smartstore/orders/_/claim-action'
  const { trigger, isLoading } = useApiMutation<unknown, Record<string, unknown>>(path, 'POST')

  // request 변경 시 입력 초기화
  useEffect(() => {
    setReason('')
    setHoldbackClassType('')
  }, [request])

  if (!request) return null

  const { event, action } = request
  const categoryLower = event.category.toLowerCase() as 'cancel' | 'return' | 'exchange'
  const needsReason = action === 'reject' || action === 'holdback'
  const needsHoldbackType = action === 'holdback'
  const isApprove = action === 'approve'

  const handleConfirm = async () => {
    if (needsReason && !reason.trim()) {
      addToast({ type: 'error', message: '사유를 입력해주세요' })
      return
    }
    if (needsHoldbackType && !holdbackClassType.trim()) {
      addToast({ type: 'error', message: '보류 분류를 입력해주세요' })
      return
    }
    const body: Record<string, unknown> = { category: categoryLower, action }
    if (needsReason) body.reason = reason.trim()
    if (needsHoldbackType) body.holdbackClassType = holdbackClassType.trim()
    const result = await trigger(body)
    if (result) {
      addToast({ type: 'success', message: `${CATEGORY_LABEL[event.category]} ${ACTION_LABEL[action]} 완료` })
      onSuccess()
    } else {
      addToast({ type: 'error', message: '처리에 실패했습니다 — 콘솔 또는 네트워크 탭 확인' })
    }
  }

  return (
    <Modal open={!!request} onClose={onClose} title={`${CATEGORY_LABEL[event.category]} ${ACTION_LABEL[action]}`} size="sm">
      <div className={styles.actionDialog}>
        <div className={styles.muted}>주문 {event.productOrderId} · {event.productName}</div>

        {isApprove && categoryLower !== 'exchange' && (
          <Alert variant="warning">
            승인 시 네이버 환불 처리 + 활성화 코드 만료 + 크레딧 회수가 자동 진행됩니다.
          </Alert>
        )}
        {isApprove && categoryLower === 'exchange' && (
          <Alert variant="info">
            교환 수거 승인은 환불을 발생시키지 않습니다 (재발송으로 진행).
          </Alert>
        )}

        {needsHoldbackType && (
          <Input
            value={holdbackClassType}
            onChange={(e) => setHoldbackClassType(e.target.value)}
            label="보류 분류 (holdbackClassType)"
            placeholder="예: RETURN_DELIVERYFEE"
          />
        )}

        {needsReason && (
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            label="사유"
            placeholder="고객에게 전달될 사유를 입력하세요"
            rows={3}
          />
        )}

        <div className={styles.actionDialogButtons}>
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>취소</Button>
          <Button variant={isApprove || action === 'reject' ? 'danger' : 'primary'} onClick={handleConfirm} loading={isLoading}>
            {ACTION_LABEL[action]}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

// ============================================================
// 헬퍼
// ============================================================

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.meta}>
      <span className={styles.metaLabel}>{label}</span>
      <span className={styles.metaValue}>{value}</span>
    </div>
  )
}

export default SmartstoreEvents
